import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { Button } from 'antd';
import { connect } from 'react-redux';
import { FormInstance } from 'antd/lib/form';
import { isEqual } from 'lodash';

import { stripe as stripePromise } from '../../../../App';
import { BlockedByFeature } from '../../../../featureToggles/features/utils';
import { IAccount } from '../../../../slices/accounts/account';
import { actions as accountListActions, selectors as accountListSelectors } from '../../../../slices/accounts/list';
import { RootState } from '../../../../slices/rootReducer';
import { colors } from '../../../../styles/variables/index';
import { SxAlert } from '../../../common/sxAlert';
import { AccountsDashboard } from '../../accountsDashboard';
import { IFormValues, PaymentForm } from './paymentForm';
import { ListParams } from '../../../../slices/utils';
import { FilterOperators, Ifilter } from '../../../common/filters/interfaces';
import {
  getCurrentRenewal,
  ICurrentRenewalParams
} from '../../../../slices/accounts/currentRenewal/currentRenewal.thunk';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';

import cssVariable from '../../../../cssExported.module.scss';
import styles from './index.module.scss';

interface AccountBillingProps {
  accountId: string;
}

interface AccountBillingStateProps {
  account: IAccount | typeof BlockedByFeature | null;
  loading: boolean;
}

interface AccountBillingState {
  isFormComplete: boolean;
  isFormTouched: boolean;
  isAddressTouched: boolean;
}

interface DispatchProps {
  fetchAccountsList: (params: ListParams<Ifilter[]>) => void;
  getCurrentRenewal: (params: ICurrentRenewalParams) => void;
}

type Props = AccountBillingProps & AccountBillingStateProps & DispatchProps;

class AccountBillingBase extends React.Component<Props, AccountBillingState> {
  private formRef: React.RefObject<FormInstance<IFormValues>>;
  constructor(props: Props) {
    super(props);
    this.state = {
      isFormComplete: false,
      isFormTouched: false,
      isAddressTouched: false
    };
    this.setFormComplete = this.setFormComplete.bind(this);
    this.setHasAdressChanged = this.setHasAdressChanged.bind(this);
    this.formRef = React.createRef<FormInstance<IFormValues>>();
  }

  componentDidMount(): void {
    const { account, accountId, fetchAccountsList } = this.props;
    if (account) {
      return;
    }
    const fetchAccountsRequest: ListParams<Ifilter[]> = {
      filters: [
        {
          field: '_id',
          operands: [accountId],
          operator: FilterOperators.isAnyOf,
          validateInputs: false
        }
      ],
      pagination: { offset: 0, limit: 25 }
    };
    fetchAccountsList(fetchAccountsRequest);
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<AccountBillingState>): void {
    const { account, getCurrentRenewal, accountId } = this.props;
    const { account: prevAccount } = prevProps;
    const { isFormTouched: prevIsFormTouched } = prevState;

    if (account !== BlockedByFeature && prevAccount !== BlockedByFeature && !isEqual(account?.currentDeal, prevAccount?.currentDeal)) {
      getCurrentRenewal({ accountId });
    }

    const isFormTouched = !!this.formRef.current?.isFieldsTouched();
    if (prevIsFormTouched !== isFormTouched) {
      this.setState({ isFormTouched });
    }
  }

  private setFormComplete(isFormComplete: boolean) {
    this.setState({ isFormComplete });
  }

  private setHasAdressChanged(hasAddressHanged: boolean) {
    this.setState({ isAddressTouched: hasAddressHanged });
  }

  private getActions() {
    const { loading, account } = this.props;
    const { isFormComplete, isFormTouched, isAddressTouched } = this.state;
    if (loading || account === BlockedByFeature || account === null) {
      return [];
    }
    const hasInvoicedDeal = account.accessType !== AccessTypes.Enterprise || account.currentDeal?.invoiced;
    const hasDealAmountEqualsZero = isFeatureEnabled(Features.DealZero) && account.currentDeal?.amount === 0;

    return [
      <Button
        type="primary"
        className={styles.button}
        disabled={!(isFormTouched || isAddressTouched) || !isFormComplete}
        onClick={this.formRef.current?.submit}
      >
        {hasInvoicedDeal || hasDealAmountEqualsZero ? 'Save' : 'Save and send invoice'}
      </Button>
    ];
  }

  private renderWarning() {
    const { account } = this.props;
    if (account === BlockedByFeature || account === null || account.billingAddress) {
      return null;
    }
    return <SxAlert
      className={styles.alert}
      type="error"
      title={
        <span className={styles.alertMessage}><b>Billing info required: </b>
          Before this account can be used, you must populate the info below so that an invoice can be sent.
        </span>
      }
    />;
  }

  private getStripeOptions() {
    const inputStyle = {
      boxShadow: `${cssVariable.shadowXS}, 0px 0px 0px 4px ${colors.primaryIndigo50}`,
      borderColor: colors.gray300
    };
    const options: StripeElementsOptions = {
      appearance: {
        variables: {
          colorText: colors.gray800,
          fontFamily: 'Source Sans Pro, sans-serif',
          fontSizeBase: '14px'
        },
        rules: {
          '.Input': {
            border: `1px solid ${colors.gray300}`,
            fontSize: '14px',
            padding: '7px 12px'
          },
          '.Input:focus': inputStyle,
          '.Input:hover': inputStyle,
          '.Label': {
            fontSize: '14px',
            fontFamily: 'Source Sans Pro, sans-serif',
            color: '#000000D9'
          }
        }
      }
    };
    return options;
  }

  render() {
    const { account } = this.props;
    if (account === BlockedByFeature || account === null) {
      return null;
    }

    return <AccountsDashboard
      actions={this.getActions()}
      contentClass={styles.content}
    >
      {this.renderWarning()}
      <Elements stripe={stripePromise} options={this.getStripeOptions()}>
        <PaymentForm
          account={account}
          setFormComplete={this.setFormComplete}
          formRef={this.formRef}
          setHasAdressChanged={this.setHasAdressChanged}
        />
      </Elements>
    </AccountsDashboard>;
  }
}

const mapStateToProps = (state: RootState, props: AccountBillingProps): AccountBillingStateProps => {
  return {
    account: accountListSelectors.getAccountById(state, props.accountId),
    loading: accountListSelectors.getLoading(state)
  };
};

const mapDispatchToProps: DispatchProps = {
  fetchAccountsList: accountListActions.fetchAccountsList,
  getCurrentRenewal: getCurrentRenewal
};

export const AccountBilling = connect(mapStateToProps, mapDispatchToProps)(AccountBillingBase);
